import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Customer } from '@interfaces/customer/customer';
import { InviteUser } from '@interfaces/user/inviteUser';
import { UpdateUser } from '@interfaces/user/updateUsers';
import { User } from '@interfaces/user/user';
import { UserRole } from '@interfaces/user/userRole';
import { Observable, from, map, mergeMap, of, retry, startWith } from 'rxjs';
import { environment } from 'src/environments/environment';

import { fixUsers } from '@services/api.fixes';
import { CustomerService } from '@services/customer.service';

@Injectable({ providedIn: 'root' })
export class UserAdminService {
  http = inject(HttpClient);
  customerService = inject(CustomerService);

  getUsers(reload = false): Observable<User[] | null> {
    let headers = new HttpHeaders();
    if (reload) {
      headers = headers.set('Cache-Control', 'no-cache');
    }
    const url = `${environment.userApi}/users`;
    return this.http.get<User[]>(url, { headers }).pipe(
      startWith(null),
      retry(3),
      map((users: any) => {
        return fixUsers(users) as User[];
      }),
    );
  }

  getUserDetails(id: string): Observable<User | null> {
    const url = `${environment.userApi}/users/${id}`;
    const headers = new HttpHeaders().set('Cache-Control', 'no-cache');
    return this.http.get<User>(url, { headers });
  }

  getCustomers(): Observable<Customer[]> {
    return this.customerService.getCustomers();
  }

  inviteUsers(inviteUsers: InviteUser[]) {
    const numberOfSimultaneousRequests = 1;
    return from(inviteUsers).pipe(
      mergeMap((inviteUser) => this.inviteUser(inviteUser), numberOfSimultaneousRequests),
    );
  }

  inviteUser(inviteUser: InviteUser) {
    const url = `${environment.userApi}/invite-user`;
    return this.http.post(url, inviteUser, { responseType: 'text' });
  }

  updateUser(user: User) {
    const {
      id,
      firstName,
      lastName,
      isBlocked,
      emailAddress,
      isUnsubscribedFromDailySummaryEmail,
      isCustomerService,
      connections,
    } = user;

    // New user
    if (!id) {
      // TODO: change from any to InviteUser when isAdmin is removed from the model
      const inviteUser: InviteUser = {
        firstName,
        lastName,
        emailAddress,
        connections,
      };

      return this.inviteUser(inviteUser);
    }

    // Update existing AD user
    if (id && id.includes('BillerudAzureAD')) {
      console.log('patching AD user');
      const updateUser: UpdateUser = {
        id,
        isBlocked,
        isCustomerService,
        connections,
        isUnsubscribedFromDailySummaryEmail,
      };
      const url = `${environment.userApi}/users/${id}`;
      return this.http.patch(url, updateUser);
    }

    // Update existing Auth0 user
    if (id) {
      const updateUser: UpdateUser = {
        id,
        firstName,
        lastName,
        isBlocked,
        connections,
        isUnsubscribedFromDailySummaryEmail,
      };
      const url = `${environment.userApi}/users/${id}`;
      return this.http.patch(url, updateUser);
    }

    return of({}); // We should never get here
  }

  deleteUser(user: User) {
    const url = `${environment.userApi}/users/${user.id}`;
    return this.http.delete(url);
  }

  getUserRoles() {
    return userRoles;
  }
}

export const userRoles: UserRole[] = [
  {
    name: $localize`Standard User (No Inquiry, No Call-off)`,
    id: 'user',
    permissions: [
      'read.product',
      'read.customer',
      'read.order',
      'read.orders',
      'read.invoice',
      'read.claim',
      'read.user',
    ],
  },
  {
    name: $localize`User with Call-off`,
    id: 'calloff',
    permissions: [
      'read.product',
      'read.customer',
      'read.order',
      'read.orders',
      'read.invoice',
      'read.calloff',
      'read.stock',
      'read.claim',
      'read.user',
    ],
  },
  {
    name: $localize`User with Inquiry`,
    id: 'inquiry',
    permissions: [
      'read.product',
      'read.customer',
      'read.order',
      'read.orders',
      'read.invoice',
      'read.claim',
      'read.inquiries',
      'read.user',
    ],
  },
  {
    name: $localize`User with Inquiry and Call-off`,
    id: 'inquirycalloff',
    permissions: [
      'read.product',
      'read.customer',
      'read.order',
      'read.orders',
      'read.invoice',
      'read.calloff',
      'read.stock',
      'read.claim',
      'read.inquiries',
      'read.user',
    ],
  },
  {
    name: $localize`User with Invoice`,
    id: 'inquiry',
    permissions: ['read.product', 'read.customer', 'read.invoice', 'read.user', 'read.invoice'],
  },
  {
    name: $localize`LPB User`,
    id: 'lpb',
    permissions: [
      'read.product',
      'read.customer',
      'read.order',
      'read.orders',
      'read.stock',
      'read.report',
      'read.user',
      'read.lpb',
      'read.forecast',
    ],
  },
  {
    name: $localize`LPB only Quality`,
    id: 'lpb',
    permissions: [
      'read.customer',
      'read.order',
      'read.orders',
      'read.stock',
      'read.report',
      'read.user',
      'read.lpb',
      'read.qualityonly',
      'read.forecast',
    ],
  },
  {
    name: $localize`LPB User with Invoice`,
    id: 'lpb',
    permissions: [
      'read.product',
      'read.customer',
      'read.order',
      'read.orders',
      'read.stock',
      'read.report',
      'read.user',
      'read.lpb',
      'read.forecast',
      'read.invoice',
    ],
  },
  {
    name: $localize`Unknown`,
    id: 'unknown',
    permissions: [],
  },

  {
    name: 'EUDR User',
    id: 'eudr',
    permissions: ['read.product', 'read.customer', 'read.eudr', 'read.user', 'write.user'],
  },
];
